/**
 * Per-type completion checks for cadence success metrics, auto-removal rules,
 * and condition steps. Used to gate the Publish / Activate buttons before
 * the request reaches the backend's typed-config validation.
 *
 * Each helper returns `{ valid: boolean, reason?: string }`. Reason is the
 * user-facing label that's surfaced in the publish error toast.
 *
 * Mirrors the field requirements declared in
 * et-cadences/cadences/model/evaluator_config/. If you add a metric_type or
 * removal_type there, mirror the rule here and add a test.
 */

const isPositiveInt = (v) => v != null && v !== '' && Number.isFinite(Number(v)) && Number(v) > 0;
const isNonEmpty = (v) => v != null && String(v).trim() !== '';

/**
 * Validate a success metric's per-type config.
 * `m.type` is the metric_type string; `m.config` is the form state object.
 */
export const isMetricComplete = (m) => {
  const type = m?.type;
  const cfg = m?.config || {};
  if (!type) return { valid: false, reason: 'metric type is missing' };

  switch (type) {
    case 'saved_search':
      return cfg.savedSearchId ? { valid: true } : { valid: false, reason: 'saved search metric: pick a saved search' };

    case 'portfolio_stage_reached': {
      if (!isPositiveInt(cfg.teamId)) return { valid: false, reason: 'portfolio stage metric: pick a portfolio team' };
      if (!isNonEmpty(cfg.stageId) && !isNonEmpty(cfg.stageName))
        return { valid: false, reason: 'portfolio stage metric: pick a stage' };
      return { valid: true };
    }

    case 'gifts_amount':
      return isPositiveInt(cfg.threshold)
        ? { valid: true }
        : { valid: false, reason: 'gifts amount metric: enter a target dollar threshold' };

    // All-optional types — empty config is allowed.
    case 'gift_given':
    case 'gifts_count':
    case 'gift_made':
    case 'meetings_count':
    case 'meeting_scheduled':
    case 'interaction_created':
    case 'email_opened':
    case 'email_replied':
    case 'email_clicked':
    case 'email_response_rate':
    case 'email_bounced':
    case 'proposal_funded':
    case 'unsubscribed':
      return { valid: true };

    default:
      // Unknown / legacy type — let the backend decide. The publish gate
      // shouldn't block on something the FE doesn't understand.
      return { valid: true };
  }
};

/**
 * Validate an auto-removal rule's per-type config.
 * `r.type` is the removal_type; `r.config` is the form state.
 */
export const isRemovalRuleComplete = (r) => {
  const type = r?.type;
  const cfg = r?.config || {};
  if (!type) return { valid: false, reason: 'removal rule type is missing' };

  switch (type) {
    case 'saved_search':
      return cfg.savedSearchId
        ? { valid: true }
        : { valid: false, reason: 'saved search removal: pick a saved search' };

    case 'portfolio_stage_reached': {
      if (!isPositiveInt(cfg.teamId)) return { valid: false, reason: 'portfolio stage removal: pick a portfolio team' };
      if (!isNonEmpty(cfg.stageId) && !isNonEmpty(cfg.stageName))
        return { valid: false, reason: 'portfolio stage removal: pick a stage' };
      return { valid: true };
    }

    case 'cadence_qualification':
      return isPositiveInt(cfg.cadenceId)
        ? { valid: true }
        : { valid: false, reason: 'cadence qualification removal: pick a cadence' };

    case 'list_membership':
      return isPositiveInt(cfg.listId)
        ? { valid: true }
        : { valid: false, reason: 'list membership removal: pick a list' };

    // Auto-enforced exits — no user-configurable payload.
    case 'unsubscribed':
    case 'unsubscribe':
    case 'bounce':
    case 'email_bounced':
    case 'gift_given':
    case 'gift_made':
    case 'interaction_created':
    case 'meetings_count':
    case 'proposal_funded':
      return { valid: true };

    default:
      return { valid: true };
  }
};

/**
 * Validate a condition step's per-type payload.
 * Accepts either `step.condition` (modern shape from add-condition-modal) or
 * the legacy `step.conditionType`-driven shape.
 */
export const isConditionComplete = (step) => {
  const cond = step?.condition;
  const type = cond?.type || step?.conditionType;
  if (!type) return { valid: false, reason: 'condition step is missing a type' };

  switch (type) {
    case 'saved_search':
      return cond?.savedSearch
        ? { valid: true }
        : { valid: false, reason: 'saved search condition: pick a saved search' };

    case 'portfolio_stage_reached':
      // Both pieces are required by the BE evaluator path.
      if (!isNonEmpty(cond?.portfolioTeamId)) return { valid: false, reason: 'portfolio stage condition: pick a team' };
      if (!isNonEmpty(cond?.portfolioStage)) return { valid: false, reason: 'portfolio stage condition: pick a stage' };
      return { valid: true };

    case 'list_membership':
      return cond?.list ? { valid: true } : { valid: false, reason: 'list membership condition: pick a list' };

    // Conditions with no required fields.
    case 'email_opened':
    case 'email_replied':
    case 'email_clicked':
    case 'interaction_created':
    case 'gift_given':
    case 'always_true':
    case 'always_false':
      return { valid: true };

    default:
      return { valid: true };
  }
};

/**
 * Walk a publish-ready cadence state and return user-facing reasons for any
 * incomplete config. Empty array means "ready to publish".
 *
 * Intended use:
 *   const issues = findIncompleteConfigs({ successMetric, additionalMetrics, removalRules, steps });
 *   if (issues.length) Alert.error(`Cannot publish: ${issues.join('; ')}`);
 */
export const findIncompleteConfigs = ({
  successMetric,
  additionalMetrics = [],
  removalRules = [],
  steps = [],
} = {}) => {
  const issues = [];

  const allMetrics = successMetric?.type ? [successMetric, ...additionalMetrics.filter((m) => m?.type)] : [];
  allMetrics.forEach((m) => {
    const r = isMetricComplete(m);
    if (!r.valid) issues.push(r.reason);
  });

  removalRules
    .filter((r) => r?.type)
    .forEach((rule) => {
      const r = isRemovalRuleComplete(rule);
      if (!r.valid) issues.push(r.reason);
    });

  // Walk top-level steps and any branches for condition-step completeness.
  const walk = (s) => {
    if (s?.type === 'condition') {
      const r = isConditionComplete(s);
      if (!r.valid) issues.push(r.reason);
    }
    if (s?.branches) {
      (s.branches.true || []).forEach(walk);
      (s.branches.false || []).forEach(walk);
    }
  };
  steps.forEach(walk);

  return issues;
};
